/**
 * Backend → frontend statement field mapping.
 *
 * Centralises the translation from the backend statement shape (as returned
 * by infrasonic's /graph endpoints) to the frontend statement shape used
 * throughout the React app. Extracted to one place so the field mapping
 * and the `'none'` sentinel convention for `topOfCommunity` stay consistent
 * across code paths.
 *
 * Consumers:
 *  - `useGraphStore.getStatements` — initial bootstrap load
 *  - `useFilterStore.createRecalculationCallbacks` — post-recalc enrichment
 *
 * Backend → frontend field map:
 *  - id                       → uid
 *  - content                  → text
 *  - contextId                → context
 *  - categories               → tags
 *  - statementHashtags        → hashtags
 *  - createdAt                → timestamp
 *  - sortId                   → sortId
 *  - statementCommunities     → statementCommunities
 *  - topStatementCommunity    → topCommunity   (int or null)
 *  - topStatementOfCommunity  → topOfCommunity (int or 'none')
 */

/**
 * Convert a backend statement into the frontend shape.
 *
 * `topOfCommunity` uses the string `'none'` as a sentinel for "not the top
 * statement in any community", matching the convention used by
 * `updateStatementCommunities` and `calculateAvailableOptions`.
 */
export function normalizeBackendStatement(backendStatement) {
    return {
        uid: backendStatement.id,
        text: backendStatement.content,
        context: backendStatement.contextId,
        tags: backendStatement.categories || [],
        hashtags: backendStatement.statementHashtags || [],
        timestamp: backendStatement.createdAt,
        sortId: backendStatement.sortId,
        visible: true,
        statementCommunities: backendStatement.statementCommunities || [],
        topCommunity:
            backendStatement.topStatementCommunity != null
                ? parseInt(backendStatement.topStatementCommunity)
                : null,
        topOfCommunity:
            backendStatement.topStatementOfCommunity &&
            backendStatement.topStatementOfCommunity !== 'none'
                ? parseInt(backendStatement.topStatementOfCommunity)
                : 'none',
    }
}

/**
 * Build a post-recalc filter-store statement.
 *
 * When `localStatement` is provided, its local-only fields (notably
 * `sentiment`, which is enriched client-side during bootstrap via
 * `processStatements`) are preserved on top of the normalised backend data.
 *
 * When `localStatement` is null — i.e. the statement was newly revealed by
 * the recalc, e.g. after removing a `context: X` stopword — a fresh record
 * is built purely from backend data. Such statements will have no
 * `sentiment` until the next bootstrap or manual sentiment refresh.
 *
 * Always sets `grayedOut: false` — a successful recalc means the statement
 * passes the current filter at the graph level.
 */
export function buildRecalculatedFilterStatement(
    backendStatement,
    localStatement = null
) {
    const normalized = normalizeBackendStatement(backendStatement)
    return {
        ...(localStatement || {}),
        ...normalized,
        grayedOut: false,
    }
}
